import { createElement } from "react";
import type { ReactElement, ReactNode } from "react";
import "./markdown.css";

/**
 * Sanitisierender Markdown-Renderer, seit dem 04.09.2026 an den mdRenderer angeglichen:
 * Parser-Logik und erzeugte Tags sind ein Port von md-renderer
 * (packages/markdown/src/parser.ts + inline.ts, Tag-Erzeugung DocumentView.tsx/InlineText.tsx).
 * Abgleichstabelle samt bewusster Ausnahmen (Mermaid/Mathe als Codeblock, kein
 * Syntax-Highlighting, FrontMatter): docs/superpowers/specs/2026-09-04-markdown-angleichung.md.
 *
 * SICHERHEITSRELEVANT: Die Eingabe ist der contentMarkdown fremder Benutzer, also
 * unvertrauenswürdiger Text. Der Renderer baut ausschliesslich React-Elemente aus einem
 * selbst geparsten Baum — im Produktionspfad gibt es KEIN `dangerouslySetInnerHTML`.
 * Dadurch maskiert React jeden Textknoten von selbst: rohes `<script>`, `<img onerror=…>`
 * oder `<b onclick=…>` können nie als aktives HTML entstehen, sie erscheinen als Text.
 * URLs gelangen nur an zwei Stellen in den Baum — Link-`href` und Bild-`src` — und beide
 * gehen strikt durch `istSichereUrl` (nur http/https/relativ/#; strenger als der
 * mdRenderer, der URLs ungeprüft setzt).
 *
 * Der `unsicher`-Schalter hängt GENAU diese beiden Wächter aus (URL-Schema-Prüfung +
 * HTML-Maskierung). Er existiert allein, damit die Sicherheitszusage prüfbar ist: In der
 * Gegenprobe (markdown.test.tsx) MUSS mit `unsicher=true` jeder Angriff aktiv werden, sonst
 * prüfte der sichere Test nichts. Die Komponente ruft `renderMarkdown` IMMER ohne diesen
 * Schalter — er darf nirgends im Anwendungscode gesetzt werden.
 */

/** Erlaubt nur relative URLs, Anker (#) sowie http/https. `javascript:`, `data:`, `mailto:`
 *  usw. fallen durch. Steuerzeichen/Leerraum werden ZUERST entfernt, weil Browser sie in
 *  URLs ignorieren — sonst schlüpfte etwa "java\tscript:alert(1)" am Schema-Test vorbei. */
export function istSichereUrl(roh: string): boolean {
  const sauber = roh.replace(/[\u0000-\u0020]/g, "");
  const schema = /^([a-z][a-z0-9+.-]*):/i.exec(sauber);
  if (schema === null) return true; // kein Schema → relativ, "/…" oder "#…"
  const name = (schema[1] ?? "").toLowerCase();
  return name === "http" || name === "https";
}

/** Extern = lädt beim Betrachten von einem fremden Host: http/https-Schema oder
 *  protokollrelativ (`//host/…`). Steuerzeichen werden wie in `istSichereUrl` ZUERST
 *  entfernt, damit " \t//host" nicht als relativ durchgeht. */
function istExterneUrl(roh: string): boolean {
  const sauber = roh.replace(/[\u0000-\u0020]/g, "");
  return /^https?:/i.test(sauber) || sauber.startsWith("//");
}

/** Ein Textknoten. Sicher: als React-Kind (maskiert). Unsicher (nur Gegenprobe): als rohes
 *  innerHTML — so wird eingebettetes HTML zum aktiven Knoten und der Angriffstest schlägt an. */
function textKnoten(text: string, unsicher: boolean, key: string): ReactNode {
  if (unsicher) {
    return <span key={key} dangerouslySetInnerHTML={{ __html: text }} />;
  }
  return text;
}

// ---------------------------------------------------------------------------
// Inline-Tokenizer — Port von md-renderer/packages/markdown/src/inline.ts.
// Unterstützt fett (**/__), kursiv (*/_), Code-Spans (`-Läufe), Links [t](url)
// und Backslash-Escapes. Unmatched Delimiter bleiben Literaltext.
// ---------------------------------------------------------------------------

type InlineKnoten =
  | { art: "text"; text: string }
  | { art: "strong"; kinder: InlineKnoten[] }
  | { art: "em"; kinder: InlineKnoten[] }
  | { art: "code"; text: string }
  | { art: "link"; url: string; kinder: InlineKnoten[] };

const ESCAPABLE = new Set(["\\", "`", "*", "_", "[", "]", "(", ")", "|", "#", "!", ">", "~", "-", "+", "."]);

const WORT_ZEICHEN = /[\p{L}\p{N}]/u;

function istWortZeichen(ch: string | undefined): boolean {
  return ch !== undefined && WORT_ZEICHEN.test(ch);
}

function istLeerraum(ch: string | undefined): boolean {
  return ch !== undefined && /\s/.test(ch);
}

/** Sucht ab `from` eine Backtick-Folge exakt der Länge `len`; liefert deren Startindex oder -1. */
function findeBacktickLauf(src: string, from: number, len: number): number {
  let i = from;
  while (i < src.length) {
    if (src[i] === "`") {
      let lauf = 1;
      while (src[i + lauf] === "`") lauf++;
      if (lauf === len) return i;
      i += lauf;
    } else {
      i++;
    }
  }
  return -1;
}

/** Versucht ab Position `i` (auf '[') einen Link `[label](url)` zu erkennen. */
function versucheLink(src: string, i: number): { label: string; url: string; ende: number } | null {
  let tiefe = 1;
  let j = i + 1;
  while (j < src.length) {
    const c = src[j];
    if (c === "\\" && j + 1 < src.length) { j += 2; continue; }
    if (c === "[") tiefe++;
    else if (c === "]") {
      tiefe--;
      if (tiefe === 0) break;
    }
    j++;
  }
  if (tiefe !== 0 || src[j + 1] !== "(") return null;
  let k = j + 2;
  let klammern = 1;
  while (k < src.length) {
    const c = src[k];
    if (c === "\\" && k + 1 < src.length) { k += 2; continue; }
    if (c === "(") klammern++;
    else if (c === ")") {
      klammern--;
      if (klammern === 0) break;
    }
    k++;
  }
  if (klammern !== 0) return null;
  return { label: src.slice(i + 1, j), url: src.slice(j + 2, k), ende: k + 1 };
}

/** Sucht den schliessenden Emphasis-Delimiter; liefert dessen Startindex oder -1. */
function findeEmphasisSchliesser(src: string, from: number, delim: string): number {
  const unterstrich = delim[0] === "_";
  let i = from;
  while (i < src.length) {
    const c = src[i];
    if (c === "\\" && i + 1 < src.length) { i += 2; continue; }
    if (c === "`") {
      // Code-Span überspringen, damit `*` darin nicht als Delimiter zählt.
      let lauf = 1;
      while (src[i + lauf] === "`") lauf++;
      const schluss = findeBacktickLauf(src, i + lauf, lauf);
      if (schluss >= 0) { i = schluss + lauf; continue; }
      i += lauf;
      continue;
    }
    if (src.startsWith(delim, i)) {
      const davor = src[i - 1];
      const danach = src[i + delim.length];
      const gueltig = !istLeerraum(davor) && (!unterstrich || !istWortZeichen(danach));
      if (gueltig && i > from) return i;
      i += delim.length;
      continue;
    }
    i++;
  }
  return -1;
}

/** Versucht ab Position `i` fett/kursiv zu erkennen (strong vor em). */
function versucheEmphasis(
  src: string,
  i: number,
  ch: string,
  lauf: number,
): { fett: boolean; inhalt: string; ende: number } | null {
  const unterstrich = ch === "_";
  // Kein Öffner mitten im Wort (schützt snake_case).
  if (unterstrich && istWortZeichen(src[i - 1])) return null;

  const versuch = (delim: string): { fett: boolean; inhalt: string; ende: number } | null => {
    const start = i + delim.length;
    const erstes = src[start];
    if (erstes === undefined || istLeerraum(erstes)) return null;
    const schluss = findeEmphasisSchliesser(src, start, delim);
    if (schluss < 0) return null;
    return { fett: delim.length === 2, inhalt: src.slice(start, schluss), ende: schluss + delim.length };
  };

  if (lauf >= 2) {
    const fett = versuch(ch + ch);
    if (fett) return fett;
  }
  return versuch(ch);
}

/** Parst Inline-Markdown zu einem Inline-Knotenbaum. */
function parseInline(src: string): InlineKnoten[] {
  const knoten: InlineKnoten[] = [];
  let text = "";
  const flush = () => {
    if (text) {
      knoten.push({ art: "text", text });
      text = "";
    }
  };

  let i = 0;
  const n = src.length;
  while (i < n) {
    const c = src[i]!;

    if (c === "\\" && i + 1 < n && ESCAPABLE.has(src[i + 1]!)) {
      text += src[i + 1];
      i += 2;
      continue;
    }

    if (c === "`") {
      let lauf = 1;
      while (src[i + lauf] === "`") lauf++;
      const schluss = findeBacktickLauf(src, i + lauf, lauf);
      if (schluss >= 0) {
        flush();
        knoten.push({ art: "code", text: src.slice(i + lauf, schluss) });
        i = schluss + lauf;
        continue;
      }
      text += src.slice(i, i + lauf);
      i += lauf;
      continue;
    }

    if (c === "[") {
      const link = versucheLink(src, i);
      if (link) {
        flush();
        knoten.push({ art: "link", url: link.url, kinder: parseInline(link.label) });
        i = link.ende;
        continue;
      }
      text += c;
      i++;
      continue;
    }

    if (c === "*" || c === "_") {
      let lauf = 1;
      while (lauf < 2 && src[i + lauf] === c) lauf++;
      const emph = versucheEmphasis(src, i, c, lauf);
      if (emph) {
        flush();
        knoten.push({
          art: emph.fett ? "strong" : "em",
          kinder: parseInline(emph.inhalt),
        });
        i = emph.ende;
        continue;
      }
      text += src.slice(i, i + lauf);
      i += lauf;
      continue;
    }

    text += c;
    i++;
  }
  flush();
  return knoten;
}

/** Klartext eines Inline-Baums (für die Leere-Kopfzeile-Prüfung der Tabelle). */
function inlineAlsKlartext(inline: InlineKnoten[]): string {
  let out = "";
  for (const k of inline) {
    switch (k.art) {
      case "text":
      case "code":
        out += k.text;
        break;
      case "strong":
      case "em":
      case "link":
        out += inlineAlsKlartext(k.kinder);
        break;
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Block-Parser — Port von md-renderer/packages/markdown/src/parser.ts.
// Die Dispatch-Reihenfolge in parse() ist verbindlich (wie in der Referenz).
// Bewusste Ausnahme: mermaid/math werden hier als Codeblock mit `sprache`
// weitergegeben statt an Fremdrenderer (siehe Spec).
// ---------------------------------------------------------------------------

type Ausrichtung = "leading" | "center" | "trailing";
type CalloutArt = "info" | "warning" | "danger";

interface ListenPunkt {
  bloecke: Block[];
}

type Block =
  | { art: "heading"; ebene: number; text: InlineKnoten[] }
  | { art: "absatz"; text: InlineKnoten[] }
  | { art: "code"; sprache: string; code: string }
  | { art: "zitat"; bloecke: Block[] }
  | { art: "callout"; sorte: CalloutArt; titel: string; bloecke: Block[] }
  | { art: "ul"; punkte: ListenPunkt[] }
  | { art: "ol"; start: number; punkte: ListenPunkt[] }
  | { art: "tabelle"; kopf: InlineKnoten[][]; ausrichtungen: Ausrichtung[];
      /** Relative Spaltenbreiten in Prozent (Pandoc-Regel, Entscheid Dirk E4) — leer =
       *  automatischer Umbruch. */
      breiten: number[];
      zeilen: InlineKnoten[][][] }
  | { art: "bild"; url: string; alt: string }
  | { art: "linie" }
  | { art: "seitenumbruch" };

/**
 * Ordnet ein `[!TOKEN]` einer Callout-Art zu (deutsch & englisch, wie im mdRenderer).
 *
 * **Nachgezogen 24.09.2026 (Controller-Nachtrag zu E1):** `GEFAHR` ist eine ZUSÄTZLICHE
 * Marke der Gefahr-Stufe, neben ACHTUNG/FEHLER/CAUTION/DANGER/ERROR — die beleg-engine
 * (`AnhangMarkdown.STUFEN`) hat diese Markentabelle wörtlich von hier übernommen und dort
 * bereits die sechzehnte Marke `GEFAHR`. Es wäre nicht erklärbar, dass `[!CAUTION]` und
 * `[!DANGER]` den Gefahrenkasten setzen, das deutsche Wort für dieselbe Stufe aber
 * wörtlicher Text bliebe. Der Port zieht die Engine hiermit nach.
 */
function calloutArtVonToken(token: string): CalloutArt | null {
  switch (token.toUpperCase()) {
    case "INFO": case "NOTE": case "TIP": case "HINWEIS": case "TIPP":
      return "info";
    case "WARNUNG": case "WARNING": case "VORSICHT": case "IMPORTANT": case "WICHTIG":
      return "warning";
    case "ACHTUNG": case "FEHLER": case "CAUTION": case "DANGER": case "ERROR": case "GEFAHR":
      return "danger";
    default:
      return null;
  }
}

function calloutStandardTitel(sorte: CalloutArt): string {
  switch (sorte) {
    case "info": return "Info";
    case "warning": return "Warnung";
    case "danger": return "Achtung";
  }
}

/**
 * Sprechender Standardtitel passend zum verwendeten Token (z. B. HINWEIS → „Hinweis").
 *
 * **Nachgezogen 24.09.2026:** `GEFAHR` bekommt denselben Vorgabetitel wie CAUTION/DANGER —
 * „Achtung", nicht „Gefahr" (wortgleich mit `AnhangMarkdown.VORGABETITEL` in der Engine).
 * Der Kasten trägt das Wort der STUFE, nicht die getippte Marke: „Gefahr" als Überschrift
 * über einem Vertragsabsatz klänge nach Lebensgefahr, gemeint ist eine scharfe Warnung.
 */
function calloutTitelZumToken(token: string): string | null {
  switch (token.toUpperCase()) {
    case "HINWEIS": case "NOTE": return "Hinweis";
    case "INFO": return "Info";
    case "TIPP": case "TIP": return "Tipp";
    case "WARNUNG": case "WARNING": return "Warnung";
    case "VORSICHT": return "Vorsicht";
    case "WICHTIG": case "IMPORTANT": return "Wichtig";
    case "ACHTUNG": return "Achtung";
    case "FEHLER": case "ERROR": return "Fehler";
    case "CAUTION": case "DANGER": case "GEFAHR": return "Achtung";
    default: return null;
  }
}

function calloutIcon(sorte: CalloutArt): string {
  switch (sorte) {
    case "info": return "ℹ︎";
    case "warning": return "⚠︎";
    case "danger": return "⛔︎";
  }
}

function zaehlePraefix(s: string, zeichen: string): number {
  let i = 0;
  while (i < s.length && s[i] === zeichen) i++;
  return i;
}

function zaehleSolange(s: string, pruefe: (c: string) => boolean): number {
  let i = 0;
  while (i < s.length && pruefe(s[i]!)) i++;
  return i;
}

class BlockParser {
  private readonly zeilen: string[];
  private index = 0;

  constructor(zeilen: string[]) {
    this.zeilen = zeilen;
  }

  parse(): Block[] {
    const bloecke: Block[] = [];
    while (this.index < this.zeilen.length) {
      if (this.zeile().trim() === "") {
        this.index++;
        continue;
      }
      let block: Block | null;
      if ((block = this.parseSeitenumbruch())) { bloecke.push(block); continue; }
      if ((block = this.parseMatheBlock())) { bloecke.push(block); continue; }
      if ((block = this.parseZaunCode())) { bloecke.push(block); continue; }
      if ((block = this.parseUeberschrift())) { bloecke.push(block); continue; }
      if ((block = this.parseTrennlinie())) { bloecke.push(block); continue; }
      if ((block = this.parseBildBlock())) { bloecke.push(block); continue; }
      if (this.istZitat(this.zeile())) { bloecke.push(this.parseZitat()); continue; }
      if ((block = this.parseTabelle())) { bloecke.push(block); continue; }
      if (this.istStrichMarker(this.zeile())) { bloecke.push(this.parseListe(false)); continue; }
      if (this.istZahlMarker(this.zeile())) { bloecke.push(this.parseListe(true)); continue; }
      bloecke.push(this.parseAbsatz());
    }
    return bloecke;
  }

  private zeile(): string {
    return this.zeilen[this.index] ?? "";
  }

  private parseZaunCode(): Block | null {
    const getrimmt = this.zeile().trim();
    let zaun: string;
    if (getrimmt.startsWith("```")) zaun = "```";
    else if (getrimmt.startsWith("~~~")) zaun = "~~~";
    else return null;

    const sprache = getrimmt.slice(3).trim();
    this.index++;
    const codeZeilen: string[] = [];
    while (this.index < this.zeilen.length) {
      if (this.zeile().trim().startsWith(zaun)) {
        this.index++;
        break;
      }
      codeZeilen.push(this.zeile());
      this.index++;
    }
    const code = codeZeilen.join("\n");
    switch (sprache.toLowerCase()) {
      case "mermaid":
        // Bewusste Ausnahme: kein Mermaid-Fremdrenderer über Nutzertext (Spec).
        return { art: "code", sprache: "mermaid", code };
      case "math":
      case "latex":
      case "tex":
        // Bewusste Ausnahme: kein KaTeX über Nutzertext (Spec).
        return { art: "code", sprache: "math", code };
      default:
        return { art: "code", sprache, code };
    }
  }

  /** Block-Mathe: eine Zeile `$$…$$` oder ein `$$`-Block über mehrere Zeilen. */
  private parseMatheBlock(): Block | null {
    const getrimmt = this.zeile().trim();
    if (!getrimmt.startsWith("$$")) return null;

    // Einzeiler: $$ ... $$
    if (getrimmt.length >= 4 && getrimmt.endsWith("$$")) {
      const inhalt = getrimmt.slice(2, -2).trim();
      this.index++;
      return { art: "code", sprache: "math", code: inhalt };
    }
    // Mehrzeiler: $$ … bis Zeile mit schliessendem $$
    const codeZeilen: string[] = [];
    const ersterRest = getrimmt.slice(2);
    if (ersterRest !== "") codeZeilen.push(ersterRest);
    this.index++;
    while (this.index < this.zeilen.length) {
      const t = this.zeile().trim();
      if (t.endsWith("$$")) {
        const rest = t.slice(0, -2);
        if (rest !== "") codeZeilen.push(rest);
        this.index++;
        break;
      }
      codeZeilen.push(this.zeile());
      this.index++;
    }
    return { art: "code", sprache: "math", code: codeZeilen.join("\n") };
  }

  private parseUeberschrift(): Block | null {
    const zeile = this.zeile();
    const rauten = zaehlePraefix(zeile, "#");
    if (rauten < 1 || rauten > 6) return null;
    const rest = zeile.slice(rauten);
    if (rest !== "" && rest[0] !== " ") return null;
    let inhalt = rest.trim();
    while (inhalt.endsWith("#")) inhalt = inhalt.slice(0, -1);
    inhalt = inhalt.trim();
    this.index++;
    return { art: "heading", ebene: rauten, text: parseInline(inhalt) };
  }

  private parseSeitenumbruch(): Block | null {
    const t = this.zeile().trim().toLowerCase();
    if (t !== "\\newpage" && t !== "\\pagebreak") return null;
    this.index++;
    return { art: "seitenumbruch" };
  }

  private parseTrennlinie(): Block | null {
    if (!this.istTrennlinie(this.zeile())) return null;
    this.index++;
    return { art: "linie" };
  }

  private parseBildBlock(): Block | null {
    const t = this.zeile().trim();
    if (!t.startsWith("![") || !t.endsWith(")")) return null;
    const mitte = t.indexOf("](");
    if (mitte < 0) return null;
    const alt = t.slice(2, mitte);
    let url = t.slice(mitte + 2, -1);
    // Optionalen Titel abschneiden:  (url "Titel")
    const leer = url.indexOf(' "');
    if (leer >= 0) url = url.slice(0, leer);
    this.index++;
    return { art: "bild", url: url.trim(), alt };
  }

  private parseZitat(): Block {
    const innen: string[] = [];
    while (this.index < this.zeilen.length) {
      const t = this.zeile().trim();
      if (!t.startsWith(">")) break;
      let inhalt = t.slice(1);
      if (inhalt.startsWith(" ")) inhalt = inhalt.slice(1);
      innen.push(inhalt);
      this.index++;
    }
    // Callout erkennen: erste Zeile "[!TYPE] optionaler Titel"
    const erste = innen[0];
    if (erste !== undefined) {
      const t = erste.trim();
      const schluss = t.indexOf("]");
      if (t.startsWith("[!") && schluss >= 0) {
        const token = t.slice(2, schluss);
        const sorte = calloutArtVonToken(token);
        if (sorte !== null) {
          const rest = t.slice(schluss + 1).trim();
          const titel = rest === "" ? (calloutTitelZumToken(token) ?? calloutStandardTitel(sorte)) : rest;
          return { art: "callout", sorte, titel, bloecke: new BlockParser(innen.slice(1)).parse() };
        }
      }
    }
    return { art: "zitat", bloecke: new BlockParser(innen).parse() };
  }

  private parseTabelle(): Block | null {
    if (this.index + 1 >= this.zeilen.length) return null;
    const kopfZeile = this.zeilen[this.index]!;
    const trennZeile = this.zeilen[this.index + 1]!;
    if (!kopfZeile.includes("|") || !this.istTrennzeile(trennZeile)) return null;

    // Die Trennzeile bestimmt die Spaltenzahl — Kopf- UND Datenzeilen werden darauf
    // gebracht (Controller-Nachtrag 24.09.2026, wortgleich mit `AnhangMarkdown.aufSpalten`
    // in der Engine, siehe `aufSpalten` unten).
    const trennZellen = this.spalteZeile(trennZeile);
    const spalten = trennZellen.length;
    const kopf = aufSpalten(this.spalteZeile(kopfZeile).map(parseInline), spalten);
    const ausrichtungen = trennZellen.map((z) => this.ausrichtung(z));
    const breiten = spaltenbreiten(trennZellen);
    this.index += 2;

    const zeilen: InlineKnoten[][][] = [];
    while (this.index < this.zeilen.length) {
      const zeile = this.zeile();
      if (zeile.trim() === "" || !zeile.includes("|")) break;
      zeilen.push(aufSpalten(this.spalteZeile(zeile).map(parseInline), spalten));
      this.index++;
    }
    return { art: "tabelle", kopf, ausrichtungen, breiten, zeilen };
  }

  private parseListe(nummeriert: boolean): Block {
    const erster = this.listenMarker(this.zeile());
    if (!erster) return this.parseAbsatz();

    const basisEinzug = erster.einzug;
    const startNummer = erster.nummer;
    const punkte: ListenPunkt[] = [];
    let aktuelleZeilen: string[] = [];
    let begonnen = false;

    const flush = () => {
      if (!begonnen) return;
      while (aktuelleZeilen.length > 0 && aktuelleZeilen[aktuelleZeilen.length - 1] === "") {
        aktuelleZeilen.pop();
      }
      punkte.push({ bloecke: new BlockParser(aktuelleZeilen).parse() });
      aktuelleZeilen = [];
    };

    while (this.index < this.zeilen.length) {
      const zeile = this.zeile();
      if (zeile.trim() === "") {
        aktuelleZeilen.push("");
        this.index++;
        continue;
      }
      const marker = this.listenMarker(zeile);
      if (marker && marker.einzug === basisEinzug && marker.nummeriert === nummeriert) {
        flush();
        begonnen = true;
        aktuelleZeilen.push(this.ersetzeTaskMarker(zeile.slice(marker.inhaltsEinzug)));
        this.index++;
      } else if (this.fuehrendeLeerzeichen(zeile) > basisEinzug) {
        // Fortsetzung oder verschachtelter Inhalt: um Marker-Breite ausrücken.
        const ausrueckung = Math.min(this.fuehrendeLeerzeichen(zeile), erster.inhaltsEinzug);
        aktuelleZeilen.push(zeile.slice(ausrueckung));
        this.index++;
      } else {
        break;
      }
    }
    flush();

    if (nummeriert) {
      return { art: "ol", start: startNummer, punkte };
    }
    return { art: "ul", punkte };
  }

  private parseAbsatz(): Block {
    const absatz: string[] = [];
    while (this.index < this.zeilen.length) {
      const zeile = this.zeile();
      if (zeile.trim() === "") break;
      // Setext-Überschrift: Textzeile(n) gefolgt von einer Unterstreichung aus = oder -.
      if (absatz.length > 0) {
        const ebene = this.setextEbene(zeile);
        if (ebene !== null) {
          this.index++;
          return { art: "heading", ebene, text: parseInline(absatz.join(" ")) };
        }
      }
      if (
        this.istUeberschriftZeile(zeile) || this.istZaun(zeile) || this.istZitat(zeile) ||
        this.istTrennlinie(zeile) || this.istStrichMarker(zeile) || this.istZahlMarker(zeile)
      ) {
        break;
      }
      if (
        this.index + 1 < this.zeilen.length &&
        zeile.includes("|") &&
        this.istTrennzeile(this.zeilen[this.index + 1]!)
      ) {
        break;
      }
      absatz.push(zeile.trim());
      this.index++;
    }
    // Wie im mdRenderer: Folgezeilen mit Leerzeichen verbinden (kein <br>).
    return { art: "absatz", text: parseInline(absatz.join(" ")) };
  }

  /** Erkennt eine Setext-Unterstreichung: nur „=" (Ebene 1) oder nur „-" (Ebene 2). */
  private setextEbene(zeile: string): number | null {
    const t = zeile.trim();
    if (t === "") return null;
    if ([...t].every((c) => c === "=")) return 1;
    if ([...t].every((c) => c === "-")) return 2;
    return null;
  }

  /** Wandelt einen führenden Task-Marker in ein Kästchen-Glyph. */
  private ersetzeTaskMarker(s: string): string {
    if (s.startsWith("[ ] ")) return "☐ " + s.slice(4);
    if (s.startsWith("[x] ") || s.startsWith("[X] ")) return "☑ " + s.slice(4);
    if (s === "[ ]") return "☐";
    if (s === "[x]" || s === "[X]") return "☑";
    return s;
  }

  // Erkennungs-Hilfen (ohne Verbrauch)

  private fuehrendeLeerzeichen(zeile: string): number {
    return zaehlePraefix(zeile, " ");
  }

  private istZaun(zeile: string): boolean {
    const t = zeile.trim();
    return t.startsWith("```") || t.startsWith("~~~");
  }

  private istUeberschriftZeile(zeile: string): boolean {
    const rauten = zaehlePraefix(zeile, "#");
    if (rauten < 1 || rauten > 6) return false;
    const rest = zeile.slice(rauten);
    return rest === "" || rest[0] === " ";
  }

  private istTrennlinie(zeile: string): boolean {
    const ohne = zeile.trim().replaceAll(" ", "");
    if (ohne.length < 3) return false;
    const zeichen = new Set(ohne);
    if (zeichen.size !== 1) return false;
    const c = [...zeichen][0]!;
    return "-*_".includes(c);
  }

  private istZitat(zeile: string): boolean {
    return zeile.trim().startsWith(">");
  }

  private istStrichMarker(zeile: string): boolean {
    const m = this.listenMarker(zeile);
    return m !== null && !m.nummeriert;
  }

  private istZahlMarker(zeile: string): boolean {
    const m = this.listenMarker(zeile);
    return m !== null && m.nummeriert;
  }

  /** Erkennt einen Listenmarker und liefert Einrückung, Startspalte des Inhalts und Typ. */
  private listenMarker(
    zeile: string,
  ): { einzug: number; inhaltsEinzug: number; nummeriert: boolean; nummer: number } | null {
    const einzug = this.fuehrendeLeerzeichen(zeile);
    const rest = zeile.slice(einzug);
    const erstesZeichen = rest[0];
    if (erstesZeichen === undefined) return null;

    if ("-*+".includes(erstesZeichen)) {
      const danach = rest.slice(1);
      if (danach[0] !== " ") return null;
      const leer = zaehlePraefix(danach, " ");
      return { einzug, inhaltsEinzug: einzug + 1 + leer, nummeriert: false, nummer: 1 };
    }

    const ziffern = zaehleSolange(rest, (c) => c >= "0" && c <= "9");
    if (ziffern > 0) {
      const trenner = rest[ziffern];
      if (trenner !== "." && trenner !== ")") return null;
      const danach = rest.slice(ziffern + 1);
      if (danach !== "" && danach[0] !== " ") return null;
      const leer = zaehlePraefix(danach, " ");
      return {
        einzug,
        inhaltsEinzug: einzug + ziffern + 1 + leer,
        nummeriert: true,
        nummer: parseInt(rest.slice(0, ziffern), 10) || 1,
      };
    }
    return null;
  }

  private spalteZeile(zeile: string): string[] {
    let s = zeile.trim();
    if (s.startsWith("|")) s = s.slice(1);
    if (s.endsWith("|")) s = s.slice(0, -1);
    // Nach Zell-Trennern splitten, dabei escaped Pipes (\|) als Literal behandeln.
    const zellen: string[] = [];
    let aktuell = "";
    let i = 0;
    while (i < s.length) {
      const c = s[i]!;
      if (c === "\\" && s[i + 1] === "|") {
        aktuell += "|";
        i += 2;
        continue;
      }
      if (c === "|") {
        zellen.push(aktuell.trim());
        aktuell = "";
        i++;
        continue;
      }
      aktuell += c;
      i++;
    }
    zellen.push(aktuell.trim());
    return zellen;
  }

  private istTrennzeile(zeile: string): boolean {
    if (!zeile.includes("-")) return false;
    const zellen = this.spalteZeile(zeile);
    if (zellen.length === 0) return false;
    for (const zelle of zellen) {
      const kern = zelle.replaceAll(":", "");
      if (kern === "" || ![...kern].every((c) => c === "-")) return false;
    }
    return true;
  }

  private ausrichtung(zelle: string): Ausrichtung {
    const links = zelle.startsWith(":");
    const rechts = zelle.endsWith(":");
    if (links && rechts) return "center";
    if (rechts) return "trailing";
    return "leading";
  }
}

/**
 * Bringt eine Tabellenzeile (Kopf ODER Datenzeile) auf die Spaltenzahl der Trennzeile:
 * überzählige Zellen fallen weg, fehlende kommen leer hinzu (Controller-Nachtrag
 * 24.09.2026, wortgleich mit `AnhangMarkdown.aufSpalten` in der Engine).
 *
 * <p>Weggelassen wird nur, was ohnehin keine Spalte hätte — eine Zelle jenseits der
 * letzten Spalte hat keine Breite und keine Ausrichtung, sonst zeigte der Bildschirm sie
 * angehängt und schief. Ein Bausteinschreiber, dem eine Zelle zu viel unterläuft, sieht
 * die fehlende Angabe in der Zeile; eine zerrissene Tabelle sähe er erst am Bildschirm.
 */
function aufSpalten(zeile: InlineKnoten[][], spalten: number): InlineKnoten[][] {
  if (zeile.length === spalten) return zeile;
  const gerade = zeile.slice(0, spalten);
  while (gerade.length < spalten) gerade.push([]);
  return gerade;
}

/**
 * So viele Bindestriche braucht JEDE Trennzelle, bevor ihre Längen als Spaltenbreiten
 * gelten (Controller-Ruling 24.09.2026) — siehe {@link spaltenbreiten}.
 */
const BREITEN_MINDESTSTRICHE = 3;
/** Keine Spalte wird schmaler als das; eine 0-%-Spalte verschluckt ihren Inhalt. */
const BREITE_MINDESTPROZENT = 1;

/**
 * Die PANDOC-REGEL für Spaltenbreiten (Entscheid Dirk E4, 24.09.2026): Sind die
 * Bindestrichläufe der Trennzeile NICHT alle gleich lang, gelten ihre Längen als RELATIVE
 * Spaltenbreiten.
 *
 * ```
 * | Leistung          | Menge | Preis |
 * |-------------------|-------|-------|   → 58 % / 21 % / 21 %
 * ```
 *
 * Gezählt werden die BINDESTRICHE, nicht die Zeichen der Zelle: `| --- | ---: | :---: |`
 * hat drei verschieden lange Zellen bei drei GLEICH langen Läufen — wer die Zellenlänge
 * messen würde, machte aus jeder Tabelle mit gemischter Ausrichtung versehentlich eine mit
 * erzwungenen Breiten, und das Gegenteil von „kein bestehender Text ändert sein Bild".
 *
 * **Die Mindestlänge (Controller-Nachtrag 24.09.2026, wortgleich mit
 * `AnhangMarkdown.colgroup` in der Engine):** Breiten entstehen nur, wenn JEDE Trennzelle
 * mindestens {@link BREITEN_MINDESTSTRICHE} Bindestriche trägt. Die Kurzschreibweisen
 * `:-:`, `--:` oder `-` sind AUSRICHTUNG, keine Breitenangabe — wer sie tippt, will eine
 * schmale Quelltextzeile, und bekäme sonst aus z. B. einem und zwei Strichen
 * stillschweigend 33 % zu 67 %. Die Ausrichtung selbst bleibt von dieser Regel unberührt.
 *
 * Gerundet wird auf ganze Prozent; die Differenz zu 100 geht auf die Spalte mit den
 * meisten Strichen. KEINE Breite unterschreitet {@link BREITE_MINDESTPROZENT}: Eine
 * 0-%-Spalte wäre eine unsichtbare Zelle, deren Inhalt im Nachbarfeld auftaucht — was die
 * Klemmung kostet, zahlt die breiteste Spalte, damit die Summe 100 bleibt.
 *
 * Sind alle Läufe gleich lang, kommt eine LEERE Liste heraus und die Tabelle bleibt beim
 * automatischen Umbruch.
 *
 * **Dieselbe Regel läuft in DREI Parsern** (Spec „Textbausteine als Anhang", Abschnitt
 * „Spaltenbreiten"): hier für den Bildschirm, in der beleg-engine (`AnhangMarkdown`) für
 * das PDF und — in Etappe 2 — im md-renderer für die Handbuch-PDFs. Die Zahlen MÜSSEN
 * gleich sein; die Tests beider Seiten nennen deshalb dieselben Beispiele.
 */
function spaltenbreiten(trennzellen: string[]): number[] {
  if (trennzellen.length < 2) return [];
  const striche = trennzellen.map((z) => [...z].filter((c) => c === "-").length);
  if (striche.some((n) => n < BREITEN_MINDESTSTRICHE)) return [];
  if (striche.every((n) => n === striche[0])) return [];
  const summe = striche.reduce((a, b) => a + b, 0);
  if (summe === 0) return [];
  const prozent = striche.map((n) =>
    Math.max(BREITE_MINDESTPROZENT, Math.round((100 * n) / summe)));
  let breiteste = 0;
  striche.forEach((n, i) => { if (n > striche[breiteste]!) breiteste = i; });
  const vergeben = prozent.reduce((a, b) => a + b, 0);
  prozent[breiteste] = Math.max(BREITE_MINDESTPROZENT, prozent[breiteste]! + (100 - vergeben));
  return prozent;
}

/** Parst einen kompletten Markdown-Text zu einer Blockliste (Tabs → 4 Leerzeichen). */
function parseBloecke(quelle: string): Block[] {
  const normalisiert = quelle.replace(/\r\n?/g, "\n").replaceAll("\t", "    ");
  return new BlockParser(normalisiert.split("\n")).parse();
}

// ---------------------------------------------------------------------------
// Rendering: Blockbaum → React-Elemente (Tags wie DocumentView.tsx/InlineText.tsx,
// Klassen kundeninfo-eigen — Zuordnung in der Spec).
// ---------------------------------------------------------------------------

const AUSRICHTUNG_CSS: Record<Ausrichtung, "left" | "center" | "right"> = {
  leading: "left",
  center: "center",
  trailing: "right",
};

/** Zerlegt einen Inline-Baum in React-Knoten; Textknoten laufen durch `textKnoten`. */
function inlineZuKnoten(inline: InlineKnoten[], unsicher: boolean, key: string): ReactNode[] {
  return inline.map((k, i) => {
    const kindKey = `${key}-${i}`;
    switch (k.art) {
      case "text":
        return textKnoten(k.text, unsicher, kindKey);
      case "strong":
        return <strong key={kindKey}>{inlineZuKnoten(k.kinder, unsicher, kindKey)}</strong>;
      case "em":
        return <em key={kindKey}>{inlineZuKnoten(k.kinder, unsicher, kindKey)}</em>;
      case "code":
        return <code key={kindKey}>{k.text}</code>;
      case "link": {
        const inhalt = inlineZuKnoten(k.kinder, unsicher, `${kindKey}-t`);
        if (!unsicher && !istSichereUrl(k.url)) {
          // Gefährliche URL: kein Anker, nur der Linktext bleibt sichtbar.
          return <span key={kindKey}>{inhalt}</span>;
        }
        return (
          <a key={kindKey} href={k.url} rel="noopener noreferrer" target="_blank">
            {inhalt}
          </a>
        );
      }
    }
  });
}

/** Liste von Blöcken in einem Wrapper-`div` — Gegenstück zur BlockList des mdRenderer. */
function bloeckeZuKnoten(bloecke: Block[], unsicher: boolean, key: string, eng: boolean): ReactNode {
  const klasse = eng ? "kn-md__bloecke kn-md__bloecke--eng" : "kn-md__bloecke";
  return (
    <div key={key} className={klasse}>
      {bloecke.map((b, i) => blockZuKnoten(b, unsicher, `${key}-${i}`))}
    </div>
  );
}

function blockZuKnoten(block: Block, unsicher: boolean, key: string): ReactNode {
  switch (block.art) {
    case "heading": {
      // Wie im mdRenderer: Ebene → h1…h6 direkt (die frühere +2-Verschiebung entfällt,
      // die Grössen zähmt das CSS der jeweiligen Ansicht).
      const tag = `h${Math.min(6, Math.max(1, block.ebene))}`;
      return createElement(
        tag,
        { key, className: "kn-md__h" },
        inlineZuKnoten(block.text, unsicher, `${key}-t`),
      );
    }
    case "absatz":
      return (
        <p key={key} className="kn-md__p">
          {inlineZuKnoten(block.text, unsicher, `${key}-t`)}
        </p>
      );
    case "code": {
      // data-language nur, wenn die Sprache wie ein Sprachkürzel aussieht (kleingeschrieben);
      // alles andere wird weggelassen — Härtung, falls das Attribut je in CSS/JS landet.
      const sprache = block.sprache.toLowerCase();
      const sprachAttribut = /^[a-z0-9+#.-]{1,30}$/.test(sprache) ? sprache : undefined;
      return (
        <pre key={key} className="kn-md__pre" data-language={sprachAttribut}>
          <code>{block.code}</code>
        </pre>
      );
    }
    case "zitat":
      return (
        <blockquote key={key} className="kn-md__zitat">
          {bloeckeZuKnoten(block.bloecke, unsicher, `${key}-b`, false)}
        </blockquote>
      );
    case "callout":
      return (
        <div key={key} className={`kn-md__callout kn-md__callout--${block.sorte}`}>
          <div className="kn-md__callout-titel">
            <span className="kn-md__callout-icon" aria-hidden="true">{calloutIcon(block.sorte)}</span>
            {textKnoten(block.titel, unsicher, `${key}-titel`)}
          </div>
          <div className="kn-md__callout-inhalt">
            {bloeckeZuKnoten(block.bloecke, unsicher, `${key}-b`, false)}
          </div>
        </div>
      );
    case "ul":
      return (
        <ul key={key} className="kn-md__ul">
          {block.punkte.map((p, j) => (
            <li key={`${key}-${j}`}>{bloeckeZuKnoten(p.bloecke, unsicher, `${key}-${j}`, true)}</li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol key={key} className="kn-md__ol" start={block.start}>
          {block.punkte.map((p, j) => (
            <li key={`${key}-${j}`}>{bloeckeZuKnoten(p.bloecke, unsicher, `${key}-${j}`, true)}</li>
          ))}
        </ol>
      );
    case "tabelle": {
      const mitKopf = !block.kopf.every((z) => inlineAlsKlartext(z).trim() === "");
      // Die Klasse `--breiten` statt einer CSS-`:has(colgroup)`-Auswahl (Entscheid aus dem
      // Task-Brief, empfohlener Weg): Die Entscheidung steht damit im TypeScript, wo sie
      // ohnehin schon getroffen wird, und nicht in einer CSS-Auswahl, die ein alter
      // Browser stillschweigend ignorieren könnte.
      const klasse = block.breiten.length > 0
        ? "kn-md__tabelle kn-md__tabelle--breiten"
        : "kn-md__tabelle";
      return (
        <div key={key} className={klasse}>
          <table>
            {block.breiten.length > 0 && (
              <colgroup>
                {block.breiten.map((b, i) => (
                  <col key={i} style={{ width: `${String(b)}%` }} />
                ))}
              </colgroup>
            )}
            {mitKopf && (
              <thead>
                <tr>
                  {block.kopf.map((zelle, i) => (
                    <th key={i} style={{ textAlign: AUSRICHTUNG_CSS[block.ausrichtungen[i] ?? "leading"] }}>
                      {inlineZuKnoten(zelle, unsicher, `${key}-k${i}`)}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {block.zeilen.map((zeile, r) => (
                <tr key={r}>
                  {zeile.map((zelle, c) => (
                    <td key={c} style={{ textAlign: AUSRICHTUNG_CSS[block.ausrichtungen[c] ?? "leading"] }}>
                      {inlineZuKnoten(zelle, unsicher, `${key}-z${r}-${c}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    case "bild": {
      // Strenger als der mdRenderer: `src` nur über istSichereUrl (Spec, Sicherheitsgrund).
      if (!unsicher && !istSichereUrl(block.url)) {
        return (
          <p key={key} className="kn-md__p">
            {textKnoten(block.alt, unsicher, `${key}-alt`)}
          </p>
        );
      }
      // Entscheid Dirk 04.09.2026 (b): externe Bilder (http/https, auch //host) laden NICHT
      // automatisch — ein Auto-Load verriete dem Notiz-Autor IP und Zeitpunkt jedes
      // Betrachters (Ausspähkanal). Stattdessen ein normaler Link; relative/same-origin
      // Bilder bleiben <img> in der figure-Struktur.
      if (!unsicher && istExterneUrl(block.url)) {
        const beschriftung = block.alt !== "" ? block.alt : block.url;
        return (
          <p key={key} className="kn-md__p">
            <a href={block.url} rel="noopener noreferrer" target="_blank">
              {textKnoten(beschriftung, unsicher, `${key}-l`)}
            </a>
          </p>
        );
      }
      return (
        <figure key={key} className="kn-md__bild">
          <img src={block.url} alt={block.alt} loading="lazy" />
          {block.alt !== "" && <figcaption>{textKnoten(block.alt, unsicher, `${key}-cap`)}</figcaption>}
        </figure>
      );
    }
    case "linie":
      return <hr key={key} className="kn-md__linie" />;
    case "seitenumbruch":
      return (
        <div key={key} className="kn-md__seitenumbruch" role="separator">
          <span>Seitenumbruch</span>
        </div>
      );
  }
}

/**
 * Nimmt den KOPF eines Textblocks weg, wenn er `nummerierung:` trägt (Paket „Überschriften-Nummerierung"):
 * `---`, höchstens 20 Zeilen `schluessel: wert`, `---`. Dieselbe Regel wie `textblockKopf` in `domain`
 * (textblockNummerierung.ts) — `web` darf `domain` nicht einführen (check-boundaries), die Fälle in
 * markdown.test.tsx sind die der Domäne. Ein Kopf ohne `nummerierung` bleibt stehen: Er ist dann eine Trennlinie
 * mit Text, wie bisher.
 */
export function ohneNummerierungsKopf(markdown: string): string {
  const ohneBom = markdown.startsWith("\uFEFF") ? markdown.slice(1) : markdown;
  const zeilen = ohneBom.split("\n");
  if (zeilen[0]?.trim() !== "---") return markdown;
  let mitNummerierung = false;
  for (let i = 1; i < zeilen.length && i <= 21; i++) {
    const zeile = zeilen[i]!.trim();
    if (zeile === "---") return mitNummerierung ? zeilen.slice(i + 1).join("\n") : markdown;
    if (zeile === "") continue;
    const treffer = /^([A-Za-z][A-Za-z0-9_-]*)[ \t]*:/.exec(zeile);
    if (treffer === null) return markdown;
    if (treffer[1]!.toLowerCase() === "nummerierung") mitNummerierung = true;
  }
  return markdown;
}

/**
 * Rendert Markdown (Konstrukt-Satz des mdRenderer) zu einem sicheren React-Baum.
 * `unsicher` NUR für die Gegenprobe im Test — siehe Kopfkommentar; im Anwendungscode
 * niemals setzen. Ein Nummerierungs-Kopf am Anfang wird nicht gezeigt (`ohneNummerierungsKopf`).
 */
export function renderMarkdown(quelle: string, unsicher = false): ReactElement {
  const bloecke = parseBloecke(ohneNummerierungsKopf(quelle));
  return (
    <div className="kn-md">
      {bloecke.map((b, i) => blockZuKnoten(b, unsicher, `b${i}`))}
    </div>
  );
}
